import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

// Get API base URL from environment variables
const API_BASE_URL = process.env.REACT_APP_SUPERADMIN_APP_API_BASE_URL;

// Async thunk for fetching recruiter information
export const fetchRecruiterInformation = createAsyncThunk(
  "recruitersTable/fetchRecruiterInformation",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/super-admin/recruiter-information`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch recruiter information");
      }

      const result = await response.json();

      if (result.success) {
        const { data } = result;

        // Transform API data to match table structure
        const transformedData = data.map((recruiter) => {
          // Combine first_name and last_name to create full name
          const firstName = recruiter.personal_information?.first_name || "";
          const lastName = recruiter.personal_information?.last_name || "";
          const fullName = `${firstName} ${lastName}`.trim();

          return {
            // Save company_id as 'id' field (similar to companies slice)
            id: recruiter.company_id ? String(recruiter.company_id) : null,
            _id: recruiter._id
              ? String(recruiter._id)
              : Math.random().toString(36),
            name: fullName || "N/A",
            email: recruiter.email || "N/A",
            phone: recruiter.personal_information?.phone || "N/A",
            jobTitle: recruiter.personal_information?.job_title || "N/A",
            recruiterLocation:
              recruiter.personal_information?.recruiter_location || "N/A",
            companyName: recruiter.personal_information?.company_name || "N/A",
            profileCompleted: recruiter.profile_completed || false,
            // Keep companyId as well for backward compatibility
            companyId: recruiter.company_id
              ? String(recruiter.company_id)
              : null,
            // Additional fields from personal_information if needed
            personalInformation: recruiter.personal_information || {},
          };
        });

        return transformedData;
      } else {
        throw new Error(
          result.message || "Failed to fetch recruiter information"
        );
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const recruitersTableSlice = createSlice({
  name: "recruitersTable",
  initialState: {
    recruiters: [],
    loading: false,
    error: null,
    totalRecruiters: 0,
    filteredRecruiters: [],
    searchQuery: "",
    filters: {
      profileCompleted: null, // null, true, false
      hasCompany: null, // null, true, false
      location: "",
    },
  },
  reducers: {
    // Clear error state
    clearError: (state) => {
      state.error = null;
    },

    // Reset recruiters data
    resetRecruitersData: (state) => {
      state.recruiters = [];
      state.filteredRecruiters = [];
      state.totalRecruiters = 0;
      state.loading = false;
      state.error = null;
      state.searchQuery = "";
      state.filters = {
        profileCompleted: null,
        hasCompany: null,
        location: "",
      };
    },

    // Set search query
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
      state.filteredRecruiters = filterRecruiters(
        state.recruiters,
        state.searchQuery,
        state.filters
      );
    },

    // Set filters
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.filteredRecruiters = filterRecruiters(
        state.recruiters,
        state.searchQuery,
        state.filters
      );
    },

    // Clear filters
    clearFilters: (state) => {
      state.filters = {
        profileCompleted: null,
        hasCompany: null,
        location: "",
      };
      state.searchQuery = "";
      state.filteredRecruiters = state.recruiters;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRecruiterInformation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecruiterInformation.fulfilled, (state, action) => {
        state.loading = false;
        state.recruiters = action.payload;
        state.filteredRecruiters = action.payload;
        state.totalRecruiters = action.payload.length;
        state.error = null;
      })
      .addCase(fetchRecruiterInformation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// Helper function to filter recruiters based on search query and filters
const filterRecruiters = (recruiters, searchQuery, filters) => {
  let filtered = recruiters;

  // Apply search query filter
  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    filtered = filtered.filter(
      (recruiter) =>
        recruiter.name.toLowerCase().includes(query) ||
        recruiter.email.toLowerCase().includes(query) ||
        recruiter.companyName.toLowerCase().includes(query) ||
        recruiter.jobTitle.toLowerCase().includes(query) ||
        recruiter.recruiterLocation.toLowerCase().includes(query)
    );
  }

  // Apply profile completion filter
  if (filters.profileCompleted !== null) {
    filtered = filtered.filter(
      (recruiter) => recruiter.profileCompleted === filters.profileCompleted
    );
  }

  // Apply company filter
  if (filters.hasCompany !== null) {
    filtered = filtered.filter((recruiter) => {
      const hasCompany = recruiter.id !== null && recruiter.id !== undefined;
      return hasCompany === filters.hasCompany;
    });
  }

  // Apply location filter
  if (filters.location) {
    filtered = filtered.filter((recruiter) =>
      recruiter.recruiterLocation
        .toLowerCase()
        .includes(filters.location.toLowerCase())
    );
  }

  return filtered;
};

// Export actions
export const {
  clearError,
  resetRecruitersData,
  setSearchQuery,
  setFilters,
  clearFilters,
} = recruitersTableSlice.actions;

// Export selectors
export const selectRecruiters = (state) => state.recruitersTable.recruiters;
export const selectFilteredRecruiters = (state) =>
  state.recruitersTable.filteredRecruiters;
export const selectRecruitersLoading = (state) => state.recruitersTable.loading;
export const selectRecruitersError = (state) => state.recruitersTable.error;
export const selectTotalRecruiters = (state) =>
  state.recruitersTable.totalRecruiters;
export const selectSearchQuery = (state) => state.recruitersTable.searchQuery;
export const selectFilters = (state) => state.recruitersTable.filters;

// Additional selector for getting recruiters by company ID
export const selectRecruitersByCompanyId = (state, companyId) =>
  state.recruitersTable.recruiters.filter(
    (recruiter) => recruiter.id === companyId
  );

// Export reducer
export default recruitersTableSlice.reducer;
